/**
 * Consent-screen view model — SEC-004 (1), FR-411.13, FR-411.25.
 *
 * Pure functions (no React, no DOM) so the phishing-relevant copy is unit-tested.
 * Every identity string shown on the consent card comes from the server's
 * /api/oauth/client-info response — never from the authorize query string and
 * never from a hard-coded brand.
 */

export interface ConsentClientInfo {
  readonly clientId: string;
  /** Registered client_name. Self-declared (and spoofable) unless `verified`. */
  readonly clientName: string;
  readonly redirectUri: string;
  readonly redirectHost: string;
  /** True only for operator-verified clients (oauth_clients.is_verified). */
  readonly verified: boolean;
}

export interface ConsentView {
  readonly title: string;
  readonly subtitle: string;
  readonly verificationLabel: 'Verified' | 'Unverified';
  /** Shown for unverified clients; null for verified ones. */
  readonly warning: string | null;
  /** Always shown, for every client (FR-411.25). */
  readonly redirectAdvisory: string;
}

/** Validate the /api/oauth/client-info body. Returns null for anything malformed. */
export function parseConsentClientInfo(body: unknown): ConsentClientInfo | null {
  if (typeof body !== 'object' || body === null) return null;
  const record = body as Record<string, unknown>;
  const clientId = record['client_id'];
  const clientName = record['client_name'];
  const redirectUri = record['redirect_uri'];
  const redirectHost = record['redirect_host'];
  const verified = record['verified'];
  if (
    typeof clientId !== 'string' || clientId.length === 0 ||
    typeof clientName !== 'string' || clientName.length === 0 ||
    typeof redirectUri !== 'string' || redirectUri.length === 0 ||
    typeof redirectHost !== 'string' || redirectHost.length === 0 ||
    typeof verified !== 'boolean'
  ) {
    return null;
  }
  return { clientId, clientName, redirectUri, redirectHost, verified };
}

export function buildConsentView(info: ConsentClientInfo): ConsentView {
  const redirectAdvisory =
    `GxP Sentinel will send you back to ${info.redirectUri} after you approve. ` +
    'If that URL looks unexpected, cancel and contact support.';

  if (info.verified) {
    return {
      title: `Authorize ${info.clientName}`,
      subtitle: `${info.clientName} is requesting access to your GxP Sentinel account.`,
      verificationLabel: 'Verified',
      warning: null,
      redirectAdvisory,
    };
  }

  // The self-declared name is never the headline: an attacker can register any
  // name, including a trusted brand (security review M1).
  return {
    title: 'Authorize an unverified application',
    subtitle: `An application calling itself "${info.clientName}" is requesting access to your GxP Sentinel account.`,
    verificationLabel: 'Unverified',
    warning:
      'GxP Sentinel has not verified this application. Its name is chosen by whoever registered it ' +
      `and may imitate a service you trust. Approving gives ${info.redirectHost} ongoing access to your ` +
      'account data. Only approve if you started this sign-in yourself and you trust ' +
      `${info.redirectHost}.`,
    redirectAdvisory,
  };
}

// ── Scopes (FR-411.13, FR-411.21) ─────────────────────────────────────────────

/** Server-defined, plain-English descriptions for the scopes this AS issues. */
const SCOPE_DESCRIPTIONS: ReadonlyMap<string, string> = new Map([
  ['mcp', 'Read FDA inspection, enforcement, and compliance data on your behalf'],
  ['mcp:read', 'Read FDA inspection, enforcement, and compliance data on your behalf (read-only)'],
]);

export interface ScopeDescription {
  /** Descriptions of recognised scopes — the only scope text shown as prose. */
  readonly known: readonly string[];
  /** Unrecognised scope tokens, listed verbatim as tokens (never as prose). */
  readonly unrecognized: readonly string[];
}

/**
 * Describe a requested scope string. The raw scope comes from the authorize
 * query string (attacker-controlled), so it is never rendered as a sentence:
 * recognised scopes map to server-defined text and anything else is listed as
 * an individual unrecognised token (security review M2).
 */
export function describeScopes(scope: string): ScopeDescription {
  const tokens = [...new Set(scope.split(' ').filter((token) => token.length > 0))];
  const known = tokens.flatMap((token) => {
    const description = SCOPE_DESCRIPTIONS.get(token);
    return description === undefined ? [] : [description];
  });
  const unrecognized = tokens.filter((token) => !SCOPE_DESCRIPTIONS.has(token));
  return { known, unrecognized };
}
